import angular from "angular";
import { Translations, OpenAPIV3, JsonSchemaUtils, JsonSchemaProperty, Objects } from "@salaxy/core";
import { diff, atomizeChangeset } from "json-diff-ts";
import { ColumnDefinition } from "tabulator-tables";
import { SxyTableController, SxyTableOptions } from "../sxy-table/SxyTableController";
import { JsonSchemaService, UiHelpers } from "../../services";
/**
 * Represents a single difference between two JSON objects.
 * Each DiffItem describes a change at a specific path in the object structure.
 * @property {string} path - The dot/bracket notation path to the property that differs.
 * @property {string} action - The action of difference (e.g., "added", "removed", "changed").
 * @property {string} actionText - The text of the action.
 * @property {any} [oldValue] - The value in the original object (if applicable).
 * @property {any} [newValue] - The value in the compared object (if applicable).
 * @property {string} [valueType] - The data type of the value (e.g., "string", "number", "object").
 */
interface DiffItem {
  path: string;
  action: string;
  actionText: string;
  oldValue?: any;
  newValue?: any;
  valueType?: string;
}

/**
 * Controller for JSON Diff component that compares two JSON objects and displays differences.
 * Uses json-diff-ts package for comparison.
 */
export class JsonDiffController implements angular.IController {
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$timeout", "JsonSchemaService", "UiHelpers"];

  /** First JSON object to compare */
  public json: any;
  /** Second JSON object to compare */
  public compareTo: any;
  /** Exclude paths from the diff */
  public excludePaths: string[] = [];
  /**
   * URL for the Open API document that contains the definition for the type.
   * The url can be an absolute url, relative url or an url with salaxy-secure/ -prefix.
   * The salaxy-secure/ -prefix will be replaced in runtime with the salaxy api server url of the curren environment.
   */
  public openApi: string;
  /** The type of the item to diff */
  public itemType: string;

  /** If true, the schema loading is ready. */
  public isLoading: boolean;

  /** Ref to used SxyTable */
  public $sxyTable: SxyTableController;

  /** References a schema document directly: the schema document is not fetched from the UI */
  public openApiDoc: any;

  /** The schema model */
  public schemaModel: OpenAPIV3.SchemaObject;

  private options = {
    selectableRows: false,
    layout: "fitColumns",
    rowFormatter: (row: any) => {
      const data = row.getData();
      if (data.action === "ADD") {
        row.getElement().style.backgroundColor = "#d4edda";
      } else if (data.action === "REMOVE") {
        row.getElement().style.backgroundColor = "#f8d7da";
      } else if (data.action === "UPDATE") {
        row.getElement().style.backgroundColor = "#fff3cd";
      }
    }
  };

  private include = [
    {title: Translations.get("SALAXY.NG1.JsonDiffComponent.path"), field: "path", minWidth: 100},
    {title: Translations.get("SALAXY.NG1.JsonDiffComponent.action"), field: "action", visible: false},
    {title: Translations.get("SALAXY.NG1.JsonDiffComponent.action"), field: "actionText",  minWidth: 100},
    {title: Translations.get("SALAXY.NG1.JsonDiffComponent.oldValue"), field: "oldValue", minWidth: 100,
      formatter: (cell: any) => this.createJsonFormatter(cell),
      cellClick: (_event: any, cell: any) => this.handleJsonCellClick(_event, cell)
    },
    {title: Translations.get("SALAXY.NG1.JsonDiffComponent.newValue"), field: "newValue", minWidth: 100,
      formatter: (cell: any) => this.createJsonFormatter(cell),
      cellClick: (_event: any, cell: any) => this.handleJsonCellClick(_event, cell)
    }
  ];

  private diffTableConfig: {json: any, compareTo: any, config: { options: SxyTableOptions, include: ColumnDefinition[], data: any[] } } = {
    json: null,
    compareTo: null,
    config: null
  };

  /**
   * Creates a new instance of the controller.
   */
  constructor(private $timeout: angular.ITimeoutService, private jsonSchemaService: JsonSchemaService, private uiHelpers: UiHelpers) {}

  /**
   * Angular lifecycle hook called on controller initialization.
   * Loads the OpenAPI schema document and finds the schema model for the specified itemType.
   * Sets the loading state while fetching the schema.
   * If itemType is not provided, does nothing.
   * @returns {Promise<void>}
   */
  public async $onInit() {
    this.openApi = this.openApi || "salaxy-secure/schemas/v02.json";
    if (this.itemType) {
      this.isLoading = true;
      this.jsonSchemaService.assureSchemaDocument(this.openApi).then((openApiDoc) => {
        this.openApiDoc = openApiDoc;
        this.schemaModel = this.jsonSchemaService.findSchema(this.openApi, this.itemType);
        this.isLoading = false;
      }).catch((e: any) => {
        console.error("Error loading schema document:", e);
        this.isLoading = false;
      });
    }
  }

  /**
   * Returns the configuration object for the diff table, including options, columns, and data.
   * - If either `json` or `compareTo` is not set, resets the diffTableConfig to null values.
   * - If the input objects have changed (deep comparison), recomputes the diff and updates the config.
   * - Triggers a table refresh after updating the config.
   * @returns { options: SxyTableOptions, include: ColumnDefinition[], data: any[] }
   * The configuration object for the SxyTable, or null if inputs are missing.
   */
  public get tableConfig(): { options: SxyTableOptions, include: ColumnDefinition[], data: any[] } {
    if (!this.json || !this.compareTo) {
      this.diffTableConfig = {
        json: null,
        compareTo: null,
        config: null
      };
    } else {
      const jsonCopy = angular.copy(this.json);
      const compareToCopy = angular.copy(this.compareTo);
      if (
        !Objects.equal(this.diffTableConfig.json, jsonCopy) ||
        !Objects.equal(this.diffTableConfig.compareTo, compareToCopy)
      ) {
        this.diffTableConfig.json = jsonCopy;
        this.diffTableConfig.compareTo = compareToCopy;
        this.diffTableConfig.config = {
          include: this.include as ColumnDefinition[],
          options: this.options as SxyTableOptions,
          data: this.computeDiff()
        };
        this.$timeout(() => {
          this.$sxyTable.refresh();
        });
      }
    }
    return this.diffTableConfig.config;
  }

  private computeDiff(): DiffItem[] {
    try {
      const changeset = diff(this.json, this.compareTo);
      return atomizeChangeset(changeset)
        .map(item => this.processDiffItem(item))
        .filter(item => item !== null) as DiffItem[];
    } catch (error) {
      console.error("Error computing diff:", error);
      return [];
    }
  }

  private processDiffItem(item: any): DiffItem | null {
    const { path, type, value, oldValue, valueType } = item;
    const normalizedPath = path.replace(/\$\./g, "");
    // Skip excluded paths
    if (this.excludePaths?.some(p => normalizedPath.startsWith(p)) || normalizedPath.startsWith("$")) {
      return null;
    }

    const displayPath = this.getDisplayPath(normalizedPath, value);
    return {
      path: displayPath,
      action: type,
      actionText: Translations.get(`SALAXY.NG1.JsonDiffComponent.actions.${type}`),
      oldValue: oldValue,
      newValue: valueType === "Object" ? JSON.stringify(value) : value,
      valueType: valueType
    };
  }

  private getDisplayPath(normalizedPath: string, value: any): string {
    if (!this.schemaModel) {
      return normalizedPath;
    }

    const schemaProp = this.getSchemaProperty(normalizedPath);
    if (!schemaProp?.schema) {
      return normalizedPath;
    }

    // Format display path based on schema type
    if (schemaProp.schema.enum?.length) {
      return `${Translations.get(`SALAXY.ENUM.${schemaProp.schema.format}.${value}.label`)} (${normalizedPath})`;
    }
    return `${Translations.get(`SALAXY.GENERATED_MODEL.${schemaProp.parentName}.${schemaProp.propertyName}.title`)} (${normalizedPath})`;
  }

  private getSchemaProperty(normalizedPath: string): JsonSchemaProperty | undefined {
    // Handle array paths like "rows[1].price"
    if (normalizedPath.includes("[") && normalizedPath.includes("]")) {
      const arrayPropertyName = normalizedPath.replace(/\[\d+\].*$/, "");
      const arraySchemaProp = JsonSchemaUtils.getProperty(this.schemaModel, arrayPropertyName);
      if (!arraySchemaProp?.schema || !JsonSchemaUtils.isArraySchemaObject(arraySchemaProp.schema)) {
        return undefined;
      }

      const remainingPath = normalizedPath.replace(/^[^[]+\[\d+\]\.?/, "");
      if (!remainingPath) {
        return arraySchemaProp;
      }

      return JsonSchemaUtils.getProperty(
        arraySchemaProp.schema.items as OpenAPIV3.SchemaObject,
        remainingPath
      );
    }
    // Handle regular paths
    return JsonSchemaUtils.getProperty(this.schemaModel, normalizedPath);
  }

  private createJsonFormatter(cell: any): any {
    const value = cell.getValue();
    if(!value) {
      return value;
    }
    if(this.isValidJsonObject(value)) {
      return `<button class="btn btn-sm btn-primary">Avaa</button>`;
    }
    return value;
  }

  private handleJsonCellClick(_event: any, cell: any): void {
    const value = cell.getValue();
    if(this.isValidJsonObject(value)) {
      const logic = {data: JSON.parse(value)};
      const template = `
        <div class="modal-header">
          <h2 class="modal-title">JSON</h2>
          <button type="button" class="btn-close" ng-click="$close('cancel')" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <salaxy-json-formatter json="$ctrl.logic.data"></salaxy-json-formatter>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-default" ng-click="$close('cancel')" sxy-text="SALAXY.UI_Terms.close"></button>
        </div>
        `;
      this.uiHelpers.openEditDialog(null, value, logic, "lg", "EditDialogController", {template});
    }
  }

  private isValidJsonObject(value: any): boolean {
    // Check if value exists and is a string
    if (!value || typeof value !== "string") {
      return false;
    }
    // Quick check for JSON object pattern
    const trimmed = value.trim();
    if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) {
      return false;
    }
    // Try to parse and validate
    try {
      const parsed = JSON.parse(trimmed);
      return typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed) &&
        Object.keys(parsed).length > 0; // Ensure it's not an empty object
    } catch {
      return false;
    }
  }
}
